import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileCheck, CreditCard, Loader2, CheckCircle, ExternalLink, 
  AlertCircle, Link2, Shield 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-banking`;

/**
 * DeployAgreementCard - Deploy signed credit agreement to blockchain
 * Shows fee breakdown (platform fee + gas) and handles Stripe payment
 */
export const DeployAgreementCard = ({ token, agreement, onDeployed }) => {
  const [status, setStatus] = useState(null);
  const [feeData, setFeeData] = useState(null);
  const [network, setNetwork] = useState('base');
  const [loading, setLoading] = useState(false);
  const [deploying, setDeploying] = useState(false);

  // Check current status
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/credit-line/agreement/onchain-status`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setStatus(r.data)).catch(() => {});
  }, [token]);

  // Calculate fee when network changes
  useEffect(() => {
    if (!token || !agreement || status?.deployed) return;
    
    setLoading(true);
    axios.post(`${API}/credit-line/agreement/deploy-fee/calculate`, 
      { network },
      { headers: { Authorization: `Bearer ${token}` } }
    ).then(r => setFeeData(r.data)).catch(e => {
      toast.error(e.response?.data?.detail || 'Could not calculate deployment fee');
    }).finally(() => setLoading(false));
  }, [token, agreement, network, status?.deployed]);

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      // If fee-exempt, use the direct deploy endpoint
      if (feeData?.fee_exempt) {
        const r = await axios.post(`${API}/credit-line/agreement/deploy-direct`,
          { network },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(r.data.message || 'Agreement deployed successfully!');
        setStatus({ ...status, deployed: true, network, tx_hash: r.data.tx_hash, contract_address: r.data.contract_address, deployed_at: new Date().toISOString() });
        if (onDeployed) onDeployed(r.data);
        setDeploying(false);
        return;
      }
      
      // Regular users go through Stripe checkout
      const r = await axios.post(`${API}/credit-line/agreement/deploy-fee/checkout`,
        { network },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Deployment failed');
      setDeploying(false);
    }
  };

  // Already deployed
  if (status?.deployed) {
    return (
      <div className="bg-slate-800 border border-emerald-500/50 rounded-xl p-6" data-testid="agreement-deployed-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-emerald-500/20 rounded-lg">
            <Shield className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Agreement On-Chain</h3>
            <p className="text-sm text-emerald-300">Deployed to {
              status.network === 'base' ? 'Base' : 
              status.network === 'arbitrum' ? 'Arbitrum' :
              status.network === 'mainnet' ? 'Ethereum Mainnet' : 'Sepolia'
            }</p>
          </div>
        </div>

        <div className="bg-slate-900/60 rounded-lg p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Agreement</span>
            <span className="text-white font-mono">{status.agreement_no}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Contract</span>
            <span className="text-purple-300 font-mono text-xs">{status.contract_address?.slice(0, 20)}...</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Deployed</span>
            <span className="text-slate-300">{new Date(status.deployed_at).toLocaleDateString()}</span>
          </div>
        </div>

        {status.tx_hash && (
          <a 
            href={status.network === 'base' ? `https://basescan.org/tx/${status.tx_hash}` 
                : status.network === 'arbitrum' ? `https://arbiscan.io/tx/${status.tx_hash}`
                : `https://${status.network === 'mainnet' ? '' : 'sepolia.'}etherscan.io/tx/${status.tx_hash}`}
            target="_blank"
            rel="noreferrer"
            className="mt-4 flex items-center justify-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm"
          >
            <ExternalLink className="w-4 h-4" /> View on {status.network === 'base' ? 'BaseScan' : status.network === 'arbitrum' ? 'Arbiscan' : 'Etherscan'}
          </a>
        )}
      </div>
    );
  }

  // No agreement signed yet
  if (!agreement) {
    return (
      <div className="bg-slate-800 border border-slate-600 rounded-xl p-6" data-testid="agreement-not-signed">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-slate-400" />
          <p className="text-slate-400">Sign your Credit Agreement first to enable on-chain deployment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-purple-500/50 rounded-xl overflow-hidden" data-testid="deploy-agreement-card">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
        <div className="flex items-center gap-3">
          <Link2 className="w-7 h-7 text-white" />
          <div>
            <h3 className="text-lg font-bold text-white">Deploy Agreement On-Chain</h3>
            <p className="text-sm text-purple-100">Register your credit agreement on Ethereum</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* Info Banner */}
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 flex gap-3">
          <FileCheck className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-purple-200">
            <p className="font-medium">Why Deploy On-Chain?</p>
            <p className="text-purple-300 mt-1">
              Deploying your credit agreement to Ethereum provides immutable proof of the agreement terms, 
              enabling transparent verification and integration with DeFi protocols.
            </p>
          </div>
        </div>

        {/* Network Selection */}
        <div>
          <label className="text-sm text-slate-400 block mb-2">Select Network</label>
          <select
            value={network}
            onChange={(e) => setNetwork(e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
            data-testid="deploy-network-select"
          >
            <option value="base">Base (Recommended)</option>
            <option value="mainnet">Ethereum Mainnet</option>
            <option value="arbitrum">Arbitrum One</option>
            <option value="sepolia">Sepolia Testnet (Testing)</option>
          </select>
        </div>

        {/* Fee Breakdown */}
        {loading ? (
          <div className="py-4 flex justify-center">
            <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
          </div>
        ) : feeData ? (
          <div className="bg-slate-700/50 rounded-lg p-4 space-y-2" data-testid="deploy-fee-breakdown">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Credit Limit</span>
              <span className="text-white">${feeData.credit_limit_usd?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Platform Fee ({feeData.platform_fee_percent}%)</span>
              <span className="text-amber-400">${feeData.platform_fee_usd?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Gas Fee (~{feeData.gas_fee_eth?.toFixed(6)} ETH)</span>
              <span className="text-blue-400">${feeData.gas_fee_usd?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm font-medium border-t border-slate-600 pt-2">
              <span className="text-slate-300">Total Deployment Fee</span>
              <span className="text-white">${feeData.total_fee_usd?.toFixed(2)}</span>
            </div>
            {feeData.eth_price_usd && (
              <p className="text-xs text-slate-500 text-right">ETH @ ${feeData.eth_price_usd?.toLocaleString()}</p>
            )}

            {!feeData.contract_deployed && (
              <div className="mt-2 flex items-center gap-2 text-amber-400 text-xs">
                <AlertCircle className="w-4 h-4" />
                <span>Contract not yet deployed on {network}. Contact admin.</span>
              </div>
            )}
          </div>
        ) : null}

        {/* Deploy Button */}
        <button
          onClick={handleDeploy}
          disabled={deploying || loading || !feeData || !feeData.contract_deployed}
          className="w-full py-3 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
          data-testid="deploy-agreement-btn"
        >
          {deploying ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : feeData?.fee_exempt ? (
            <Shield className="w-4 h-4" />
          ) : (
            <CreditCard className="w-4 h-4" />
          )}
          {feeData?.fee_exempt ? (
            `Deploy to ${network === 'base' ? 'Base' : network === 'arbitrum' ? 'Arbitrum' : network === 'mainnet' ? 'Mainnet' : 'Sepolia'} (Fee Waived)`
          ) : (
            `Pay $${feeData?.total_fee_usd?.toFixed(2) || '...'} & Deploy to ${
              network === 'base' ? 'Base' : 
              network === 'arbitrum' ? 'Arbitrum' : 
              network === 'mainnet' ? 'Mainnet' : 'Sepolia'
            }`
          )}
        </button>

        <p className="text-xs text-slate-500 text-center">
          {feeData?.fee_exempt 
            ? `Your agreement will be permanently recorded on ${network === 'base' ? 'Base' : network === 'arbitrum' ? 'Arbitrum' : 'Ethereum'}.`
            : 'After payment, your agreement will be permanently recorded.'
          }
        </p>
      </div>
    </div>
  );
};

export default DeployAgreementCard;
